import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useParams } from 'react-router-dom'
import API from '../../../../../services/API'
import DetailsBox from '../../../../../components/Cards/DetailsBox'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { LinearProgress } from '@mui/material'
import { GlobalContext } from '../../../../../App'

const SingleIncident = () => {
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [record, setRecord] = useState(null)
  const [fetchError, setFetchError] = useState(false)
  const [notes, setNotes] = useState('')
  const [reviewed, setReviewed] = useState(false)
  const { setToast } = useContext(GlobalContext)

  const recordInProgress = record?.review?.status !== 'Submitted'

  const loadIncident = () => {
    setLoading(true)
    API.get('/client-portal/single-riddor-report/' + id)
      .then((res) => {
        if (res.status === 200) {
          setRecord(res.data)
          setNotes(res.data.incident?.healthSafetyEventOrganiserNotes ?? '')
          setReviewed(
            res.data.incident?.healthSafetyConcernReviewedByOrganiser ?? false
          )
        } else {
          setFetchError(true)
        }
      })
      .catch((e) => {
        console.error(e)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadIncident()
  }, [])

  const handleSaveChanges = () => {
    API.post('/client-portal/update-riddor-notes', {
      recordID: id,
      reviewed,
      notes,
    })
      .then((res) => {
        if (res.status === 200) {
          loadIncident()
          setToast({
            message: 'Changes Saved',
          })
        }
      })
      .catch(() => {
        setToast({
          message: 'Unable to save. Please try again later.',
          severity: 'error',
        })
      })
  }

  return (
    <Stack gap={2}>
      <Typography variant="title2">Health & Safety Incident</Typography>
      {loading && <LinearProgress />}
      {fetchError && (
        <Alert severity="error">
          Unable to load Incident. Please try again later.
        </Alert>
      )}
      {recordInProgress && (
        <Alert severity="warning">
          <AlertTitle>Patient Record in Progress</AlertTitle>This patient record
          is still in progress. Please check again later before printing.
        </Alert>
      )}
      <DetailsBox
        title="Event Details"
        singleColumn={true}
        rows={[
          {
            label: 'Event',
            value: (
              <Link href={`/events/${record?.incident?.linkedEvent?._id}`}>
                {record?.incident?.linkedEvent?.title ?? ''}
              </Link>
            ),
          },
          {
            label: 'Incident Date',
            value: moment(record?.incident?.incidentDate).format(
              'Do MMM YYYY HH:mm'
            ),
          },
          {
            label: 'Record Completed By',
            value:
              record?.review?.completedBy?.firstName ??
              '' + ' ' + record?.review?.completedBy?.lastName ??
              '',
          },
          {
            label: 'Record Status',
            value: record?.review?.status ?? 'In Progress',
          },
        ]}
      />
      <DetailsBox
        title="Incident Details"
        singleColumn={true}
        fullWidthLabels={true}
        rows={[
          {
            label: 'Incident Location',
            value: record?.incident?.incidentLocation ?? '',
          },
          { label: 'Reported By', value: record?.incident?.reportedBy ?? '' },
          {
            label: 'Reported Complaint',
            value: record?.incident?.reportedComplaint ?? '',
          },
          {
            label: 'H&S Incident Type',
            value: record?.incident?.healthSafetyType ?? [].join(', '),
          },
          {
            label: 'Details',
            value: record?.incident?.healthSafetyDetails ?? '',
          },
          {
            label: 'Immediate Action Taken',
            value: record?.incident?.healthSafetyImmediateActionTaken ?? '',
          },
          {
            label: 'Event Team Notified',
            value: record?.incident?.healthSafetyEventOrganiserNotified
              ? 'Yes'
              : 'No',
          },
          {
            label: 'Additional Notes',
            value:
              record?.incident?.healthSafetyAdditionalNotesForOrganiser ?? '',
          },
          { label: 'Patient Outcome', value: record?.plan?.ptOutcome ?? '' },
        ]}
      />
      <DetailsBox title="Review">
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Event Organiser Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          helperText={!notes && 'Optional'}
        />
        <FormControlLabel
          label="Mark as Reviewed"
          control={
            <Checkbox
              checked={reviewed}
              onChange={(e) => setReviewed(e.target.checked)}
            />
          }
        />
      </DetailsBox>
      <Button
        onClick={handleSaveChanges}
        disabled={
          reviewed ===
            !!record?.incident?.healthSafetyConcernReviewedByOrganiser &&
          notes === (record?.incident?.healthSafetyEventOrganiserNotes ?? '')
        }
        color="success"
        fullWidth
        variant="contained"
      >
        Save Changes
      </Button>
    </Stack>
  )
}

export default SingleIncident
